import type { Article, ArticleTag } from './articles';

export type ArticleSection = {
  heading: string;
  body: string[];
};

export type ArticleRelated = {
  text: string;
  label: string;
  to: string;
};

export type ArticleNext = {
  slug: string;
  title: string;
};

export type ArticleBody = {
  intro: string;
  sections: ArticleSection[];
  summary: string[];
  cta: string;
  ctaText?: string;
  datePublished: string;
  dateModified: string;
  updatedLabel: string;
  related?: ArticleRelated;
  nextArticle?: ArticleNext;
  image?: string;
};

export type ApiArticle = Article & ArticleBody;

export type ParseArticleResult =
  | { ok: true; article: ApiArticle }
  | { ok: false; error: string };

export type ParseArticleCollectionResult =
  | { ok: true; articles: ApiArticle[] }
  | { ok: false; error: string };

const ARTICLE_TAGS = new Set<ArticleTag>(['Juridisch', 'Werkgever', 'Preventie', 'Basis']);
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MONTHS_NL = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
];

const DEFAULT_CTA = 'Wil je weten wat PuntUit voor jouw organisatie kan betekenen?';
const DEFAULT_CTA_TEXT = 'Meer dan 500 organisaties gingen je voor. Plan vandaag een kennismaking.';

export function isArticleTag(value: unknown): value is ArticleTag {
  return typeof value === 'string' && ARTICLE_TAGS.has(value as ArticleTag);
}

export function mergeArticles(base: Article[], extra: Article[]): Article[] {
  const extraBySlug = new Map(extra.map((article) => [article.slug, article]));
  const baseSlugs = new Set(base.map((article) => article.slug));
  const apiOnly = extra.filter((article) => !baseSlugs.has(article.slug));
  const mergedBase = base.map((article) => extraBySlug.get(article.slug) ?? article);
  return [...apiOnly, ...mergedBase];
}

export function extractArticleInputs(input: unknown): unknown[] | null {
  if (Array.isArray(input)) return input;
  if (!isRecord(input)) return null;
  if (Array.isArray(input.articles)) return input.articles;
  if (typeof input.slug === 'string') return [input];
  return null;
}

export function parseArticleCollection(
  input: unknown,
  reservedSlugs: Iterable<string> = [],
): ParseArticleCollectionResult {
  const items = extractArticleInputs(input);
  if (!items || items.length === 0) {
    return { ok: false, error: 'Body moet één artikel zijn, of { "articles": [...] } zoals het kennisbank-exportbestand.' };
  }

  const reserved = new Set(reservedSlugs);
  const articles: ApiArticle[] = [];

  for (const [index, item] of items.entries()) {
    const parsed = parseArticleInput(item, reserved);
    if (!parsed.ok) {
      return { ok: false, error: `Artikel ${index + 1}: ${parsed.error}` };
    }
    reserved.add(parsed.article.slug);
    articles.push(parsed.article);
  }

  return { ok: true, articles };
}

export function parseArticleInput(input: unknown, reservedSlugs: Iterable<string> = []): ParseArticleResult {
  const reserved = reservedSlugs instanceof Set ? reservedSlugs : new Set(reservedSlugs);
  if (!isRecord(input)) {
    return { ok: false, error: 'Body moet een JSON-object zijn.' };
  }

  const slug = readSlug(input.slug);
  if (!slug) {
    return { ok: false, error: 'slug is verplicht en mag alleen kleine letters, cijfers en koppeltekens bevatten.' };
  }
  if (reserved.has(slug)) {
    return { ok: false, error: `slug "${slug}" is al in gebruik voor een bestaand artikel.` };
  }

  const tag = input.tag ?? input.category;
  if (!isArticleTag(tag)) {
    return { ok: false, error: 'category of tag moet een van Juridisch, Werkgever, Preventie of Basis zijn.' };
  }

  const title = readRequiredString(input.title, 8, 180);
  if (!title) {
    return { ok: false, error: 'title is verplicht (8–180 tekens).' };
  }

  const desc = readRequiredString(input.desc ?? input.description, 20, 320);
  if (!desc) {
    return { ok: false, error: 'description of desc is verplicht (20–320 tekens).' };
  }

  const intro = readRequiredString(input.intro, 20, 2000);
  if (!intro) {
    return { ok: false, error: 'intro is verplicht (20–2000 tekens).' };
  }

  const sections = readSections(input.sections);
  if (!sections) {
    return { ok: false, error: 'sections moet een lijst zijn met minstens één kop en alinea’s (paragraphs of body).' };
  }

  const summary = readStringList(input.summary, 8, 280);
  if (!summary) {
    return { ok: false, error: 'summary moet een lijst zijn met minstens één samenvattingsregel.' };
  }

  const cta =
    input.ctaHeading === undefined && input.cta === undefined
      ? DEFAULT_CTA
      : readRequiredString(input.ctaHeading ?? input.cta, 8, 180);
  if (!cta) {
    return { ok: false, error: 'ctaHeading of cta moet 8–180 tekens zijn.' };
  }

  const ctaText =
    input.ctaText === undefined ? DEFAULT_CTA_TEXT : readRequiredString(input.ctaText, 8, 280);
  if (!ctaText) {
    return { ok: false, error: 'ctaText moet 8–280 tekens zijn.' };
  }

  const datePublished = readDate(input.datePublished) ?? todayIsoDate();
  const dateModified = readDate(input.dateModified) ?? datePublished;
  if (!datePublished || !dateModified) {
    return { ok: false, error: 'datePublished en dateModified moeten YYYY-MM-DD zijn.' };
  }

  const updatedLabel =
    input.updatedLabel === undefined
      ? monthYearLabel(dateModified)
      : readRequiredString(input.updatedLabel, 3, 40);
  if (!updatedLabel) {
    return { ok: false, error: 'updatedLabel moet 3–40 tekens zijn.' };
  }

  const nextArticle = readNextArticle(input.nextArticle);
  if (input.nextArticle !== undefined && !nextArticle) {
    return { ok: false, error: 'nextArticle moet slug en title bevatten.' };
  }

  const related = input.related === undefined ? relatedFromNext(nextArticle ?? undefined) : readRelated(input.related);
  if (input.related !== undefined && !related) {
    return { ok: false, error: 'related moet text, label en to bevatten.' };
  }

  const image = input.image === undefined ? undefined : readRequiredString(input.image, 8, 400);
  if (input.image !== undefined && !image) {
    return { ok: false, error: 'image moet een geldige URL of pad zijn.' };
  }

  const readTime =
    input.readingTimeMinutes !== undefined
      ? readReadTime(input.readingTimeMinutes)
      : input.readTime === undefined
        ? estimateReadTime(intro, sections, summary)
        : readReadTime(input.readTime);
  if (readTime === null) {
    return { ok: false, error: 'readingTimeMinutes of readTime moet een geheel getal tussen 1 en 60 zijn.' };
  }

  return {
    ok: true,
    article: {
      slug,
      tag,
      title,
      desc,
      readTime,
      intro,
      sections,
      summary,
      cta,
      ctaText,
      datePublished,
      dateModified,
      updatedLabel,
      ...(related ? { related } : {}),
      ...(nextArticle ? { nextArticle } : {}),
      ...(image ? { image } : {}),
    },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readSlug(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const slug = value.trim();
  if (slug.length < 3 || slug.length > 180 || !SLUG_PATTERN.test(slug)) return null;
  return slug;
}

function readRequiredString(value: unknown, min: number, max: number): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (text.length < min || text.length > max) return null;
  return text;
}

function readStringList(value: unknown, min: number, max: number): string[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const items: string[] = [];
  for (const item of value) {
    const text = readRequiredString(item, min, max);
    if (!text) return null;
    items.push(text);
  }
  return items;
}

function readSections(value: unknown): ArticleSection[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const sections: ArticleSection[] = [];
  for (const item of value) {
    if (!isRecord(item)) return null;
    const heading = readRequiredString(item.heading, 3, 180);
    const body = readStringList(item.paragraphs ?? item.body, 8, 2000);
    if (!heading || !body) return null;
    sections.push({ heading, body });
  }
  return sections;
}

function readRelated(value: unknown): ArticleRelated | undefined | null {
  if (value === undefined || value === null) return undefined;
  if (!isRecord(value)) return null;
  const text = readRequiredString(value.text, 8, 280);
  const label = readRequiredString(value.label, 3, 120);
  const to = readRequiredString(value.to, 1, 180);
  if (!text || !label || !to || !to.startsWith('/')) return null;
  return { text, label, to };
}

function readNextArticle(value: unknown): ArticleNext | undefined | null {
  if (value === undefined || value === null) return undefined;
  if (!isRecord(value)) return null;
  const slug = readSlug(value.slug);
  const title = readRequiredString(value.title, 8, 180);
  if (!slug || !title) return null;
  return { slug, title };
}

function relatedFromNext(nextArticle: ArticleNext | undefined): ArticleRelated | undefined {
  if (!nextArticle) return undefined;
  return {
    text: nextArticle.title,
    label: nextArticle.title,
    to: `/kennisbank/${nextArticle.slug}`,
  };
}

function readDate(value: unknown): string | null | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'string' || !DATE_PATTERN.test(value)) return null;
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) return null;
  return value;
}

function readReadTime(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > 60) return null;
  return value;
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthYearLabel(isoDate: string): string {
  const month = Number(isoDate.slice(5, 7)) - 1;
  const year = isoDate.slice(0, 4);
  return `${MONTHS_NL[month] ?? 'januari'} ${year}`;
}

function estimateReadTime(intro: string, sections: ArticleSection[], summary: string[]): number {
  const text = [
    intro,
    ...sections.flatMap((section) => [section.heading, ...section.body]),
    ...summary,
  ].join(' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.min(60, Math.round(words / 200) || 1));
}
